import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { registerMixpanelSuperProperties } from '@monorepo/client-common';
import { useUpdateUserLanguage } from '../api/mutations/use-user-mutations';

const I18N_STORAGE_KEY = 'i18nextLng';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const updateLanguageMutation = useUpdateUserLanguage();

  const localStorageLanguage =
    typeof window !== 'undefined'
      ? localStorage.getItem(I18N_STORAGE_KEY)
      : null;

  const browserLanguage =
    typeof window !== 'undefined' && navigator.language
      ? navigator.language.split('-')[0]
      : null;

  const changeLanguage = useCallback(
    async (languageCode: string) => {
      await i18n.changeLanguage(languageCode);
      if (typeof window !== 'undefined') {
        localStorage.setItem(I18N_STORAGE_KEY, languageCode);
      }

      // Update Mixpanel super property immediately
      registerMixpanelSuperProperties({
        hub_language: languageCode.toLowerCase(),
      });

      // Try to persist to backend when logged in; ignore errors so we always redirect
      try {
        await updateLanguageMutation.mutateAsync(languageCode);
      } catch {
        // Not logged in or API error – still redirect to language path
      }

      // Always reload to the language path so the URL and app stay in sync
      const currentPath = window.location.pathname;
      const pathWithoutLanguage = currentPath.replace(/^\/[a-z]{2}/, '');
      window.location.href = `/${languageCode}${pathWithoutLanguage}${window.location.search}`;
    },
    [i18n, updateLanguageMutation],
  );

  return {
    language: i18n.language,
    resolvedLanguage: i18n.resolvedLanguage,
    localStorageLanguage,
    browserLanguage,
    changeLanguage,
  };
};
